import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, LabelList, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { EaChartCard } from "./EaUi";
import { COLORS, MONTHS_IT, fmtQty } from "./eaFormat";
import { NAME, priceDigits } from "./eaFixUtil";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

function Annual({ a, d }) {
  const items = [["Consumo annuo", `${fmtQty(a.consumption, 0)} ${d.volume_unit}`], ["Volume fissato", `${fmtQty(a.fixed, 0)} ${d.volume_unit}`],
    ["% fissata annua", a.pct_fixed != null ? `${fmtQty(a.pct_fixed, 1)}%` : "—"], ["Prezzo medio fixing annuo", a.avg_price != null ? `${fmtQty(a.avg_price, priceDigits(d.unit))} ${d.unit}` : "—"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid={`ea-fix-cov-annual-${d.commodity}`}>
      {items.map(([l, v]) => <div key={l} className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div><div className="font-display font-bold text-slate-900 font-mono-nums">{v}</div></div>)}
    </div>
  );
}

function CovTable({ d, dg }) {
  const a = d.annual, vu = d.volume_unit;
  const row = (l, m, tid, total) => (
    <tr key={tid} className={total ? "border-t-2 border-slate-400 bg-slate-50 font-semibold text-slate-900" : "border-t border-slate-100"} data-testid={tid} {...(total ? { "data-pdf-total": true } : {})}>
      <td className="px-3 py-1.5 font-sans">{l}</td><td className="px-3 py-1.5 text-right">{fmtQty(m.consumption, 0)}</td><td className="px-3 py-1.5 text-right">{fmtQty(m.fixed, 0)}</td>
      <td className="px-3 py-1.5 text-right">{fmtQty(m.consumption - m.fixed, 0)}</td><td className="px-3 py-1.5 text-right">{m.pct_fixed != null ? `${fmtQty(m.pct_fixed, 1)}%` : "—"}</td>
      <td className="px-3 py-1.5 text-right">{m.avg_price != null ? fmtQty(m.avg_price, dg) : "—"}</td>
    </tr>
  );
  return (
    <div className="mt-4 overflow-x-auto" data-testid={`ea-fix-cov-table-${d.commodity}`}>
      <table className="w-full text-sm font-mono-nums">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>{["Mese", `Consumo ${vu}`, `Fissato ${vu}`, `Aperto ${vu}`, "% fissata", `Prezzo medio fixing ${d.unit}`].map((h, i) => <th key={h} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody>{d.monthly.map((m) => row(MONTHS_IT[m.month - 1], m, `ea-fix-cov-row-${m.month}`))}{row(`Totale ${d.year}`, a, "ea-fix-cov-total", true)}</tbody>
      </table>
    </div>
  );
}

export function EaFixCoverage({ commodity, year, params, unit, v }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ea/budget/fixings/coverage", { params: { ...params, commodity, year, unit } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [commodity, year, unit, params.company_id, params.codes, v]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const dg = priceDigits(d.unit);
  const color = commodity === "POWER" ? COLORS.power : COLORS.gas;
  const data = d.monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1], priceLbl: m.avg_price != null ? fmtQty(m.avg_price, dg) : "" }));
  return (
    <EaChartCard title={`Copertura fixing · ${NAME[commodity]} ${year}`} subtitle={`Barre = consumo mensile (${d.volume_unit}) fissato e aperto · linea = % fissata · etichette = prezzo medio di fixing (${d.unit})`} testid={`ea-fix-coverage-${commodity}`}>
      <Annual a={d.annual} d={d} />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 22 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="label" {...axis} />
            <YAxis yAxisId="v" {...axis} width={70} tickFormatter={(x) => fmtQty(x, 0)} />
            <YAxis yAxisId="p" orientation="right" {...axis} domain={[0, 100]} tickFormatter={(x) => `${x}%`} width={45} />
            <Tooltip formatter={(x, n, it) => (n === "% fissata" ? [`${fmtQty(x, 1)}%`, n] : [`${fmtQty(x, 0)} ${d.volume_unit}`, n])}
              labelFormatter={(l, p) => { const m = p?.[0]?.payload; return `${l}${m?.avg_price != null ? ` · prezzo medio fixing ${fmtQty(m.avg_price, dg)} ${d.unit}` : ""}`; }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="v" dataKey="fixed" name="Fissato" stackId="a" fill={color} />
            <Bar yAxisId="v" dataKey="open" name="Aperto" stackId="a" fill="#94A3B8"><LabelList dataKey="priceLbl" position="top" style={{ fontSize: 10, fill: "#0F172A", fontWeight: 600 }} /></Bar>
            <Line yAxisId="p" dataKey="pct_fixed" name="% fissata" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <CovTable d={d} dg={dg} />
    </EaChartCard>
  );
}
